import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  SHEET_MAX_COLS,
  SHEET_MAX_ROWS,
  cellName,
  columnName,
  displayValue,
  evaluate,
  parseNumber,
  recalculate,
  type SheetCell,
  type SheetCellFormat,
  type SheetPresence,
  type SheetTab,
} from '@itsappy/shared';
import { useSocket } from '@/realtime/socketContext';
import { useGrowSheetTab } from '@/hooks/queries';
import { useAuth } from '@/store/auth';
import { toast } from '@/store/ui';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui';
import { SheetToolbar } from './SheetToolbar';

/**
 * A collaborative spreadsheet grid.
 *
 * The tab's cells are held in a Map keyed by `r:c` rather than a dense array,
 * because a sheet is mostly empty and the grid is redrawn on every keystroke.
 * Edits go out per cell over the socket and arrive the same way; the whole
 * tab is recalculated locally after each change so a formula updates as you
 * watch someone else fill in the number it depends on.
 */

/* How much a press of "add rows"/"add columns" adds. A screenful rather than
   one at a time: somebody who has run out of grid needs room to work, not one
   more line. */
const ROW_STEP = 50;
const COLUMN_STEP = 10;

const CELL_WIDTH = 108;
const CELL_HEIGHT = 30;
const HEADER_WIDTH = 52;

const key = (r: number, c: number) => `${r}:${c}`;

export interface SheetGridProps {
  sheetId: string;
  tab: SheetTab;
  /** False for a viewer who may read the sheet but not type in it. */
  canEdit: boolean;
  /** Notifies the page when a live edit changes this tab's stored cells. */
  onCellsChanged?: (tabId: string, cells: SheetCell[]) => void;
  className?: string;
}

interface Selection {
  r: number;
  c: number;
  /** The other corner of a dragged or shift-extended range. */
  toR: number;
  toC: number;
}

const normalizeRange = (selection: Selection) => ({
  r0: Math.min(selection.r, selection.toR),
  r1: Math.max(selection.r, selection.toR),
  c0: Math.min(selection.c, selection.toC),
  c1: Math.max(selection.c, selection.toC),
});

export function SheetGrid({
  sheetId,
  tab,
  canEdit,
  onCellsChanged,
  className,
}: SheetGridProps) {
  const { socket } = useSocket();
  const me = useAuth((state) => state.user);

  const [cells, setCells] = useState<Map<string, SheetCell>>(new Map());
  const [selection, setSelection] = useState<Selection>({ r: 0, c: 0, toR: 0, toC: 0 });
  const [editing, setEditing] = useState<{ r: number; c: number; draft: string } | null>(null);
  const [peers, setPeers] = useState<Map<string, SheetPresence>>(new Map());
  const [dragging, setDragging] = useState(false);

  const editorRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const cellsRef = useRef(cells);
  cellsRef.current = cells;

  /* ----------------------------- tab contents ---------------------------- */

  useEffect(() => {
    setCells(new Map(tab.cells.map((cell) => [key(cell.r, cell.c), cell])));
    setSelection({ r: 0, c: 0, toR: 0, toC: 0 });
    setEditing(null);
  }, [tab.id, tab.cells]);

  const lookup = useCallback(
    (r: number, c: number) => cellsRef.current.get(key(r, c))?.v ?? undefined,
    [],
  );

  /**
   * Applies a change and recalculates every formula in the tab.
   *
   * Recalculating the whole tab on each edit is deliberate: a dependency
   * graph would be faster, but at these sizes the cost is invisible, and it
   * means a cell can never be left showing a stale total because the change
   * that invalidated it arrived from someone else's keyboard.
   */
  const applyLocal = useCallback(
    (mutate: (next: Map<string, SheetCell>) => void) => {
      setCells((current) => {
        const next = new Map(current);
        mutate(next);
        const recalculated = recalculate([...next.values()]);
        const rebuilt = new Map(recalculated.map((cell) => [key(cell.r, cell.c), cell]));
        onCellsChanged?.(tab.id, recalculated);
        return rebuilt;
      });
    },
    [onCellsChanged, tab.id],
  );

  /* ------------------------------- realtime ------------------------------ */

  useEffect(() => {
    if (!socket) return;

    const onCell = (p: { sheetId: string; tabId: string; cell: SheetCell }) => {
      if (p.sheetId !== sheetId || p.tabId !== tab.id) return;
      applyLocal((next) => next.set(key(p.cell.r, p.cell.c), p.cell));
    };

    const onClear = (p: { sheetId: string; tabId: string; at: { r: number; c: number }[] }) => {
      if (p.sheetId !== sheetId || p.tabId !== tab.id) return;
      applyLocal((next) => {
        for (const point of p.at) next.delete(key(point.r, point.c));
      });
    };

    const onCursor = (p: SheetPresence & { sheetId: string }) => {
      if (p.sheetId !== sheetId) return;
      setPeers((current) => new Map(current).set(p.userId, p));
    };

    const onLeft = (p: { sheetId: string; userId: string }) => {
      if (p.sheetId !== sheetId) return;
      setPeers((current) => {
        const next = new Map(current);
        next.delete(p.userId);
        return next;
      });
    };

    socket.on('sheet:cell', onCell);
    socket.on('sheet:clear', onClear);
    socket.on('sheet:cursor', onCursor);
    socket.on('sheet:left', onLeft);
    return () => {
      socket.off('sheet:cell', onCell);
      socket.off('sheet:clear', onClear);
      socket.off('sheet:cursor', onCursor);
      socket.off('sheet:left', onLeft);
    };
  }, [socket, sheetId, tab.id, applyLocal]);

  /* Tell the others where the cursor is, but not on every arrow key. */
  useEffect(() => {
    if (!socket || !me) return;
    const timer = setTimeout(() => {
      socket.emit('sheet:cursor', { sheetId, tabId: tab.id, r: selection.r, c: selection.c });
    }, 120);
    return () => clearTimeout(timer);
  }, [socket, me, sheetId, tab.id, selection.r, selection.c]);

  /* ------------------------------- editing ------------------------------- */

  const commit = useCallback(
    (r: number, c: number, raw: string) => {
      const input = raw.trim();
      const existing = cellsRef.current.get(key(r, c));

      if (!input) {
        if (!existing) return;
        applyLocal((next) => next.delete(key(r, c)));
        socket?.emit('sheet:clear', { sheetId, tabId: tab.id, at: [{ r, c }] });
        return;
      }

      // The value is computed here so it is stored alongside the input: a
      // client that cannot evaluate the formula still has something to show.
      const result = evaluate(input, lookup);
      const cell: SheetCell = {
        r,
        c,
        input,
        v: result.value,
        ...(result.error ? { error: result.error } : {}),
        ...(existing?.format ? { format: existing.format } : {}),
        updatedBy: me?.id ?? '',
        updatedAt: Date.now(),
      };

      applyLocal((next) => next.set(key(r, c), cell));
      socket?.emit('sheet:cell', { sheetId, tabId: tab.id, cell });
    },
    [applyLocal, lookup, me?.id, sheetId, socket, tab.id],
  );

  const clearRange = useCallback(() => {
    const { r0, r1, c0, c1 } = normalizeRange(selection);
    const at: { r: number; c: number }[] = [];
    for (let r = r0; r <= r1; r += 1) {
      for (let c = c0; c <= c1; c += 1) {
        if (cellsRef.current.has(key(r, c))) at.push({ r, c });
      }
    }
    if (!at.length) return;
    applyLocal((next) => {
      for (const point of at) next.delete(key(point.r, point.c));
    });
    socket?.emit('sheet:clear', { sheetId, tabId: tab.id, at });
  }, [applyLocal, selection, sheetId, socket, tab.id]);

  const startEditing = useCallback(
    (r: number, c: number, seed?: string) => {
      if (!canEdit) return;
      // Editing shows the formula, not the number it came to.
      setEditing({ r, c, draft: seed ?? cellsRef.current.get(key(r, c))?.input ?? '' });
    },
    [canEdit],
  );

  useEffect(() => {
    if (editing) editorRef.current?.focus();
  }, [editing]);

  const move = useCallback(
    (dr: number, dc: number, extend = false) => {
      setSelection((current) => {
        const r = Math.max(0, Math.min(tab.rows - 1, (extend ? current.toR : current.r) + dr));
        const c = Math.max(0, Math.min(tab.cols - 1, (extend ? current.toC : current.c) + dc));
        return extend ? { ...current, toR: r, toC: c } : { r, c, toR: r, toC: c };
      });
    },
    [tab.rows, tab.cols],
  );

  /**
   * Pasting a block of tab-separated text — the clipboard format every
   * spreadsheet uses — fills the grid from the selected cell outwards, so
   * copying a column out of Excel and into here does what it looks like.
   */
  const handlePaste = useCallback(
    (text: string) => {
      if (!canEdit) return;
      const rows = text.replace(/\r\n?/g, '\n').replace(/\n$/, '').split('\n');
      if (!rows.length) return;

      const written: SheetCell[] = [];
      const now = Date.now();
      rows.forEach((line, dr) => {
        line.split('\t').forEach((raw, dc) => {
          const r = selection.r + dr;
          const c = selection.c + dc;
          if (r >= SHEET_MAX_ROWS || c >= SHEET_MAX_COLS) return;
          const input = raw.trim();
          if (!input) return;
          const result = evaluate(input, lookup);
          written.push({
            r,
            c,
            input,
            v: result.value,
            ...(result.error ? { error: result.error } : {}),
            updatedBy: me?.id ?? '',
            updatedAt: now,
          });
        });
      });
      if (!written.length) return;

      applyLocal((next) => {
        for (const cell of written) next.set(key(cell.r, cell.c), cell);
      });
      for (const cell of written) socket?.emit('sheet:cell', { sheetId, tabId: tab.id, cell });
    },
    [applyLocal, canEdit, lookup, me?.id, selection.r, selection.c, sheetId, socket, tab.id],
  );

  /** Copying out uses the same tab-separated format, so it pastes into Excel. */
  const copyRange = useCallback(() => {
    const { r0, r1, c0, c1 } = normalizeRange(selection);
    const lines: string[] = [];
    for (let r = r0; r <= r1; r += 1) {
      const line: string[] = [];
      for (let c = c0; c <= c1; c += 1)
        line.push(displayValue(cellsRef.current.get(key(r, c))));
      lines.push(line.join('\t'));
    }
    return lines.join('\n');
  }, [selection]);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (editing) return;

    const { key: pressed, shiftKey, metaKey, ctrlKey } = event;
    const mod = metaKey || ctrlKey;

    if (mod && pressed.toLowerCase() === 'c') {
      void navigator.clipboard?.writeText(copyRange()).catch(() => undefined);
      return;
    }

    switch (pressed) {
      case 'ArrowUp':
        event.preventDefault();
        return move(-1, 0, shiftKey);
      case 'ArrowDown':
        event.preventDefault();
        return move(1, 0, shiftKey);
      case 'ArrowLeft':
        event.preventDefault();
        return move(0, -1, shiftKey);
      case 'ArrowRight':
        event.preventDefault();
        return move(0, 1, shiftKey);
      case 'Tab':
        event.preventDefault();
        return move(0, shiftKey ? -1 : 1);
      case 'Enter':
        event.preventDefault();
        return startEditing(selection.r, selection.c);
      case 'Backspace':
      case 'Delete':
        event.preventDefault();
        return canEdit ? clearRange() : undefined;
      case 'Escape':
        return setSelection((current) => ({ ...current, toR: current.r, toC: current.c }));
      default:
        break;
    }

    // Typing a printable character replaces the cell, as in every spreadsheet.
    if (!mod && pressed.length === 1) {
      event.preventDefault();
      startEditing(selection.r, selection.c, pressed);
    }
  };

  const finishEditing = (advance: 'down' | 'right' | 'none') => {
    if (!editing) return;
    commit(editing.r, editing.c, editing.draft);
    setEditing(null);
    if (advance === 'down') move(1, 0);
    if (advance === 'right') move(0, 1);
    gridRef.current?.focus();
  };

  /* ------------------------------ formatting ----------------------------- */

  /**
   * Applies a format change across the selected range.
   *
   * An empty cell in the range gets a cell of its own rather than being
   * skipped: a shaded header row with a gap in it where nobody typed anything
   * is not what the person who shaded it asked for.
   */
  const formatRange = useCallback(
    (changes: Partial<SheetCellFormat>) => {
      if (!canEdit) return;
      const { r0, r1, c0, c1 } = normalizeRange(selection);
      const now = Date.now();
      const written: SheetCell[] = [];

      for (let r = r0; r <= r1; r += 1) {
        for (let c = c0; c <= c1; c += 1) {
          const existing = cellsRef.current.get(key(r, c));
          const format = { ...existing?.format, ...changes };
          /* Keys set back to their default are dropped rather than stored as
             `undefined`, so "no fill" leaves a cell with no format at all
             rather than one carrying an empty object around. */
          const cleaned = Object.fromEntries(
            Object.entries(format).filter(
              ([, value]) => value !== undefined && value !== false,
            ),
          ) as SheetCellFormat;

          written.push({
            r,
            c,
            input: existing?.input ?? '',
            v: existing?.v ?? null,
            ...(existing?.error ? { error: existing.error } : {}),
            ...(Object.keys(cleaned).length ? { format: cleaned } : {}),
            updatedBy: me?.id ?? '',
            updatedAt: now,
          });
        }
      }

      applyLocal((next) => {
        for (const cell of written) next.set(key(cell.r, cell.c), cell);
      });
      for (const cell of written) socket?.emit('sheet:cell', { sheetId, tabId: tab.id, cell });
    },
    [applyLocal, canEdit, me?.id, selection, sheetId, socket, tab.id],
  );

  /**
   * Strips the formatting off the range, keeping what is in it.
   *
   * A cell that was only ever a colour — nothing typed in it — is removed
   * rather than left behind as an empty cell, which is what "clear" means to
   * the person pressing it.
   */
  const clearFormatting = useCallback(() => {
    if (!canEdit) return;
    const { r0, r1, c0, c1 } = normalizeRange(selection);
    const written: SheetCell[] = [];
    const emptied: { r: number; c: number }[] = [];

    for (let r = r0; r <= r1; r += 1) {
      for (let c = c0; c <= c1; c += 1) {
        const existing = cellsRef.current.get(key(r, c));
        if (!existing?.format) continue;
        if (!existing.input) {
          emptied.push({ r, c });
          continue;
        }
        const { format: _dropped, ...rest } = existing;
        written.push({ ...rest, updatedBy: me?.id ?? '', updatedAt: Date.now() });
      }
    }

    if (written.length) {
      applyLocal((next) => {
        for (const cell of written) next.set(key(cell.r, cell.c), cell);
      });
      for (const cell of written) socket?.emit('sheet:cell', { sheetId, tabId: tab.id, cell });
    }
    if (emptied.length) {
      applyLocal((next) => {
        for (const point of emptied) next.delete(key(point.r, point.c));
      });
      socket?.emit('sheet:clear', { sheetId, tabId: tab.id, at: emptied });
    }
  }, [applyLocal, canEdit, me?.id, selection, sheetId, socket, tab.id]);

  /**
   * Totals the selection, in the cell just past it.
   *
   * A column of numbers is totalled underneath and a row of them beside,
   * because that is where a total goes — and it is written as a formula, so it
   * keeps up when the numbers change. A single selected cell has nothing to
   * total, so it totals the run of numbers immediately above it instead: that
   * is what "sum" means at the bottom of a column somebody has just typed.
   */
  const sumSelection = useCallback(() => {
    if (!canEdit) return;
    const { r0, r1, c0, c1 } = normalizeRange(selection);
    const single = r0 === r1 && c0 === c1;

    if (single) {
      let top = r0;
      while (
        top > 0 &&
        parseNumber(cellsRef.current.get(key(top - 1, c0))?.input ?? '') !== null
      ) {
        top -= 1;
      }
      if (top === r0) {
        toast.error(
          'Nothing to total',
          'Select the numbers, or put the cursor under a column of them.',
        );
        return;
      }
      commit(r0, c0, `=SUM(${cellName(top, c0)}:${cellName(r0 - 1, c0)})`);
      return;
    }

    const down = r1 - r0 >= c1 - c0;
    const at = down ? { r: r1 + 1, c: c0 } : { r: r0, c: c1 + 1 };
    if (at.r >= SHEET_MAX_ROWS || at.c >= SHEET_MAX_COLS) return;

    commit(at.r, at.c, `=SUM(${cellName(r0, c0)}:${cellName(r1, c1)})`);
    setSelection({ r: at.r, c: at.c, toR: at.r, toC: at.c });
  }, [canEdit, commit, selection]);

  const copyToClipboard = useCallback(() => {
    void navigator.clipboard?.writeText(copyRange()).catch(() => undefined);
  }, [copyRange]);

  /**
   * Grows the grid.
   *
   * Through the server rather than locally, because the extent is stored on
   * the tab: growing it only in this browser would give one person rows that
   * nobody else has, and they would vanish on reload.
   */
  const grow = useGrowSheetTab();
  const growBy = useCallback(
    (what: 'rows' | 'cols') => {
      if (!canEdit) return;
      const next =
        what === 'rows'
          ? { rows: Math.min(SHEET_MAX_ROWS, tab.rows + ROW_STEP) }
          : { cols: Math.min(SHEET_MAX_COLS, tab.cols + COLUMN_STEP) };

      grow.mutate(
        { id: sheetId, tabId: tab.id, ...next },
        {
          onError: () =>
            toast.error(
              "Couldn't add those",
              'This tab is already as large as a sheet can get.',
            ),
        },
      );
    },
    [canEdit, grow, sheetId, tab.cols, tab.id, tab.rows],
  );

  /* ------------------------------- rendering ----------------------------- */

  const range = useMemo(() => normalizeRange(selection), [selection]);

  const peersByCell = useMemo(() => {
    const map = new Map<string, SheetPresence[]>();
    for (const peer of peers.values()) {
      if (peer.tabId !== tab.id || peer.userId === me?.id) continue;
      const at = key(peer.r, peer.c);
      map.set(at, [...(map.get(at) ?? []), peer]);
    }
    return map;
  }, [peers, tab.id, me?.id]);

  const active = cells.get(key(selection.r, selection.c));

  const columns = Array.from({ length: tab.cols }, (_, index) => index);
  const rows = Array.from({ length: tab.rows }, (_, index) => index);

  const selectedRange =
    range.r0 === range.r1 && range.c0 === range.c1
      ? cellName(range.r0, range.c0)
      : `${cellName(range.r0, range.c0)}:${cellName(range.r1, range.c1)}`;

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <SheetToolbar
        range={selectedRange}
        format={active?.format}
        canEdit={canEdit}
        onFormat={formatRange}
        onClearFormat={clearFormatting}
        onSum={sumSelection}
        onCopy={copyToClipboard}
        onClear={clearRange}
        onGrow={growBy}
      />

      {/* Formula bar: the address, and what was actually typed in the cell. */}
      <div className="flex items-center gap-2 border-b border-surface-3 bg-surface-2 px-2 py-1.5">
        <span className="w-16 shrink-0 rounded bg-surface-1 px-2 py-1 text-center text-xs font-medium text-slate-300">
          {cellName(selection.r, selection.c)}
        </span>
        <span aria-hidden className="text-xs text-slate-600">
          ƒ
        </span>
        <input
          value={editing ? editing.draft : (active?.input ?? '')}
          readOnly={!canEdit}
          onChange={(event) =>
            setEditing({ r: selection.r, c: selection.c, draft: event.target.value })
          }
          onFocus={() => canEdit && !editing && startEditing(selection.r, selection.c)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              finishEditing('down');
            }
            if (event.key === 'Escape') {
              setEditing(null);
              gridRef.current?.focus();
            }
          }}
          placeholder={canEdit ? 'Type a value, or =SUM(A1:A9)' : 'Read only'}
          className="min-w-0 flex-1 rounded bg-transparent px-1 py-1 text-xs text-ink outline-none placeholder:text-slate-600"
        />
      </div>

      <div
        ref={gridRef}
        tabIndex={0}
        role="grid"
        aria-label="Spreadsheet"
        onKeyDown={onKeyDown}
        onPaste={(event) => {
          if (!canEdit) return;
          event.preventDefault();
          handlePaste(event.clipboardData.getData('text/plain'));
        }}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
        className="min-h-0 flex-1 overflow-auto outline-none focus:ring-1 focus:ring-inset focus:ring-brand-500/40"
      >
        <div className="inline-block min-w-full">
          {/* Column headers, pinned so they survive a long scroll. */}
          <div className="sticky top-0 z-20 flex bg-surface-2">
            <div
              className="sticky left-0 z-30 shrink-0 border-b border-r border-surface-3 bg-surface-2"
              style={{ width: HEADER_WIDTH, height: CELL_HEIGHT }}
            />
            {columns.map((c) => (
              <div
                key={c}
                className={cn(
                  'shrink-0 border-b border-r border-surface-3 px-1 text-center text-[11px] font-medium leading-[30px]',
                  c >= range.c0 && c <= range.c1
                    ? 'bg-brand-500/15 text-brand-200'
                    : 'text-slate-400',
                )}
                style={{ width: tab.colWidths?.[c] ?? CELL_WIDTH, height: CELL_HEIGHT }}
              >
                {columnName(c)}
              </div>
            ))}
          </div>

          {rows.map((r) => (
            <div key={r} className="flex">
              <div
                className={cn(
                  'sticky left-0 z-10 shrink-0 border-b border-r border-surface-3 bg-surface-2 text-center text-[11px] leading-[30px]',
                  r >= range.r0 && r <= range.r1
                    ? 'bg-brand-500/15 text-brand-200'
                    : 'text-slate-400',
                )}
                style={{ width: HEADER_WIDTH, height: CELL_HEIGHT }}
              >
                {r + 1}
              </div>

              {columns.map((c) => {
                const cell = cells.get(key(r, c));
                const inRange =
                  r >= range.r0 && r <= range.r1 && c >= range.c0 && c <= range.c1;
                const isAnchor = r === selection.r && c === selection.c;
                const isEditing = editing?.r === r && editing?.c === c;
                const visitors = peersByCell.get(key(r, c));
                // Numbers sit right unless the cell says otherwise — that is
                // what makes a column of figures readable at a glance.
                const numeric = typeof cell?.v === 'number';
                const width = tab.colWidths?.[c] ?? CELL_WIDTH;

                return (
                  <div
                    key={c}
                    role="gridcell"
                    aria-selected={isAnchor}
                    onMouseDown={(event) => {
                      if (event.button !== 0) return;
                      setEditing(null);
                      setDragging(true);
                      setSelection({ r, c, toR: r, toC: c });
                      gridRef.current?.focus();
                    }}
                    onMouseEnter={() => {
                      if (dragging) setSelection((current) => ({ ...current, toR: r, toC: c }));
                    }}
                    onDoubleClick={() => startEditing(r, c)}
                    className={cn(
                      'relative shrink-0 select-none border-b border-r border-surface-3 px-1.5 text-xs leading-[29px]',
                      inRange && !isAnchor && 'bg-brand-500/10',
                      isAnchor && 'z-10 ring-2 ring-inset ring-brand-400',
                      cell?.error ? 'text-rose-400' : 'text-ink',
                      cell?.format?.bold && 'font-semibold',
                      cell?.format?.italic && 'italic',
                    )}
                    style={{
                      width,
                      height: CELL_HEIGHT,
                      textAlign: cell?.format?.align ?? (numeric ? 'right' : 'left'),
                      background: cell?.format?.bg,
                      color: cell?.error ? undefined : cell?.format?.color,
                    }}
                  >
                    {isEditing ? (
                      <input
                        ref={editorRef}
                        value={editing.draft}
                        onChange={(event) => setEditing({ r, c, draft: event.target.value })}
                        onBlur={() => finishEditing('none')}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            finishEditing('down');
                          }
                          if (event.key === 'Tab') {
                            event.preventDefault();
                            finishEditing('right');
                          }
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            setEditing(null);
                            gridRef.current?.focus();
                          }
                        }}
                        className="absolute inset-0 z-20 w-full bg-surface-1 px-1.5 text-xs text-ink outline-none ring-2 ring-inset ring-brand-400"
                      />
                    ) : (
                      <span className="block truncate">{displayValue(cell)}</span>
                    )}

                    {/* Someone else's cursor, in the colour they show up as everywhere. */}
                    {visitors?.length ? (
                      <span
                        className="pointer-events-none absolute -top-px left-0 right-0 bottom-0 rounded-[1px] ring-2 ring-inset"
                        style={{ ['--tw-ring-color' as string]: visitors[0].color }}
                      >
                        <span
                          className="absolute -top-4 right-0 whitespace-nowrap rounded px-1 text-[10px] leading-4 text-white"
                          style={{ background: visitors[0].color }}
                        >
                          {visitors.length > 1
                            ? `${visitors[0].name} +${visitors.length - 1}`
                            : visitors[0].name}
                        </span>
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <SheetStatusBar
        cells={cells}
        selection={selection}
        peers={peers}
        tabId={tab.id}
        meId={me?.id}
      />
    </div>
  );
}

/**
 * The running totals along the bottom. Selecting a column of numbers and
 * being told what they add up to is most of what a spreadsheet is for, and it
 * costs nothing to answer without being asked.
 */
function SheetStatusBar({
  cells,
  selection,
  peers,
  tabId,
  meId,
}: {
  cells: Map<string, SheetCell>;
  selection: Selection;
  peers: Map<string, SheetPresence>;
  tabId: string;
  meId?: string;
}) {
  const stats = useMemo(() => {
    const { r0, r1, c0, c1 } = normalizeRange(selection);
    const numbers: number[] = [];
    let filled = 0;
    for (let r = r0; r <= r1; r += 1) {
      for (let c = c0; c <= c1; c += 1) {
        const cell = cells.get(key(r, c));
        if (!cell) continue;
        filled += 1;
        if (typeof cell.v === 'number') numbers.push(cell.v);
        else if (typeof cell.v === 'string') {
          const parsed = parseNumber(cell.v);
          if (parsed !== null) numbers.push(parsed);
        }
      }
    }
    const total = numbers.reduce((sum, n) => sum + n, 0);
    return { filled, count: numbers.length, total };
  }, [cells, selection]);

  const here = [...peers.values()].filter(
    (peer) => peer.tabId === tabId && peer.userId !== meId,
  );

  return (
    <div className="flex items-center gap-3 border-t border-surface-3 bg-surface-2 px-2 py-1 text-[11px] text-slate-400">
      {stats.count > 0 && (
        <>
          <span>
            Sum {Number.isInteger(stats.total) ? stats.total : stats.total.toFixed(2)}
          </span>
          <span>
            Average{' '}
            {Number.isInteger(stats.total / stats.count)
              ? stats.total / stats.count
              : (stats.total / stats.count).toFixed(2)}
          </span>
          <span>Count {stats.count}</span>
        </>
      )}
      {stats.count === 0 && stats.filled > 0 && <span>{stats.filled} filled</span>}

      <span className="ml-auto flex items-center gap-1.5">
        {here.slice(0, 4).map((peer) => (
          <span key={peer.userId} className="flex items-center gap-1" title={peer.name}>
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: peer.color }}
            />
            <span className="max-w-24 truncate">{peer.name}</span>
          </span>
        ))}
        {here.length > 4 && <span>+{here.length - 4}</span>}
      </span>
    </div>
  );
}

/** Shown while the socket is still handing over the tabs. */
export function SheetGridSkeleton() {
  return (
    <div className="grid h-full place-items-center">
      <Spinner />
    </div>
  );
}
